<template>
    <div v-if="detail && canReadTechnicianAddress" class="mc-tech-popup" data-testid="technician-edit-panel">
        <header class="mc-tech-popup__header" :class="areaHeaderClass">
            <div class="mc-tech-popup__title-row">
                <div>
                    <h3 class="mc-tech-popup__name">{{ detail.name }}</h3>
                    <div class="mc-tech-popup__status">
                        <span class="area-badge" :class="areaBadgeClass">{{ form.area || 'West' }}</span>
                    </div>
                </div>
                <button class="mc-tech-popup__close" @click="handleCancel">
                    <i class="mc-fa icon-outline_close"></i>
                </button>
            </div>
        </header>

        <div class="mc-tech-popup__body">
            <div class="form-group">
                <label>Nama Teknisi</label>
                <input :value="form.name" type="text" class="form-control form-control-sm bg-light" disabled />
            </div>

            <div class="form-group">
                <label>Area <span class="required-mark">*</span></label>
                <select v-model="form.area" class="form-select form-select-sm" :disabled="!canSave"
                    @change="clearFieldError('area')">
                    <option value="West">West (Jakarta)</option>
                    <option value="East">East (Surabaya)</option>
                </select>
                <div v-if="fieldErrors.area" class="field-error">{{ fieldErrors.area }}</div>
            </div>

            <div class="form-group">
                <label>Home address <span class="required-mark">*</span></label>
                <!-- <div class="input-group">
                    <input v-model="form.address" type="text" class="form-control form-control-sm"
                        placeholder="Masukkan alamat rumah..." :disabled="!canSave"
                        @input="clearFieldError('address')" />
                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="geocodeAddress"
                        :disabled="!form.address || !canSave">
                        <i class="fas fa-search-location"></i> Cari
                    </button>
                </div> -->
                <TechnicianAddressSearch v-model="form.address" @select="onAddressSelected" />
                <div v-if="fieldErrors.address" class="field-error">{{ fieldErrors.address }}</div>
            </div>

            <div class="mb-3">
                <small class="text-muted d-block mt-2" style="font-size: 0.7rem;">
                    <i class="fas fa-info-circle"></i> Jika lokasi belum tepat, geser pin pada peta lalu klik
                    "Konfirmasi Alamat" untuk memperbarui alamat.
                </small>
            </div>
            <div class="row g-2 mb-3">
                <div class="col-6">
                    <label class="form-label-sm">Latitude <span class="required-mark">*</span></label>
                    <input v-model="form.lat" type="text" class="form-control form-control-sm" placeholder="ex: -6.2088"
                        :disabled="!canSave" @input="clearFieldError('lat')" @paste="onPasteCoordinates" />
                    <div v-if="fieldErrors.lat" class="field-error">{{ fieldErrors.lat }}</div>
                </div>
                <div class="col-6">
                    <label class="form-label-sm">Longitude <span class="required-mark">*</span></label>
                    <input v-model="form.lng" type="text" class="form-control form-control-sm"
                        placeholder="ex: 106.8456" :disabled="!canSave" @input="clearFieldError('lng')"
                        @paste="onPasteCoordinates" />
                    <div v-if="fieldErrors.lng" class="field-error">{{ fieldErrors.lng }}</div>
                </div>
            </div>

            <!-- Tombol Konfirmasi Alamat dipindah ke bawah -->
            <div class="mb-3">
                <button type="button" class="btn btn-sm btn-outline-primary w-100" @click="applyManualCoords"
                    :disabled="!canSave">
                    <i class="fas fa-map-marker-alt me-1"></i> Konfirmasi Alamat
                </button>
            </div>

            <div class="form-group">
                <label>
                    Radius (km) <span class="required-mark">*</span>
                </label>
                <input v-model.number="form.radius" type="number" step="1" class="form-control form-control-sm"
                    :disabled="!canSave" @input="clearFieldError('radius')" />
                <div v-if="fieldErrors.radius" class="field-error">{{ fieldErrors.radius }}</div>
            </div>
        </div>

        <footer class="mc-tech-popup__footer">
            <button type="button" class="mc-tech-popup__action" @click="handleCancel">
                Batal
            </button>
            <button v-if="canSave" type="button" class="mc-tech-popup__action mc-tech-popup__action--primary"
                @click="saveProfile" :disabled="saving">
                <i class="fas fa-save" aria-hidden="true"></i>
                {{ saving ? 'Menyimpan...' : 'Simpan Profile' }}
            </button>
        </footer>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TechnicianAddressSearch from './TechnicianAddressSearch.vue';

export default {
    name: 'TechnicianEditPanel',
    components: { TechnicianAddressSearch },
    props: {
        detail: { type: Object, required: true },
    },
    data() {
        return {
            form: {
                name: '',
                area: 'West',
                address: '',
                lat: '',
                lng: '',
                radius: 80
            },
            fieldErrors: {
                area: '',
                address: '',
                lat: '',
                lng: '',
                radius: ''
            },
            saving: false,
        };
    },
    computed: {
        areaHeaderClass() {
            const area = this.form.area || 'West';
            return area === 'West' ? 'is-west' : 'is-east';
        },
        areaBadgeClass() {
            const area = this.form.area || 'West';
            return area === 'West' ? 'badge-west' : 'badge-east';
        },
        ...mapGetters({
            hasAccessAll: 'identity/hasAccessAll',
        }),

        hasAddress() {
            return !!this.detail?.user_address_id;
        },

        canSave() {
            return this.hasAccessAll(['sms-technician-address:update']);
        },

        canReadTechnicianAddress() {
            return this.hasAccessAll(['sms-technician-address:read']);
        },
    },
    watch: {
        detail: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.$nextTick(() => this.resetForm());
                }
            }
        },
        // Amati perubahan koordinat dari map picker (via store)
        // Pantau perubahan koordinat dari draggable marker
        '$store.state.map.draggableMarkerPosition': {
            handler(newPos) {
                if (newPos && this.canSave) {
                    this.form.lat = this.formatCoordinate(newPos.lat)
                    this.form.lng = this.formatCoordinate(newPos.lng)
                    this.clearFieldError('lat')
                    this.clearFieldError('lng')
                }
            },
            deep: true,
        },
        validUsersWithRadius: {
            handler() {
                this.$nextTick(() => {
                    this.mapObject?.invalidateSize();
                });
            },
            deep: true
        },
    },
    beforeDestroy() {
        // Matikan mode picker jika panel ditutup
        this.$store.commit('map/SET_PICK_MODE', false)

        // Reset state terkait teknisi yang dipilih
        this.$store.commit('map/SET_SELECTED_TECHNICIAN', null);
        this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', null);
    },
    methods: {
        onPasteCoordinates(event) {
            // Ambil teks yang akan ditempel
            const pasted = (event.clipboardData || window.clipboardData).getData('text');
            if (!pasted) return;

            // Pecah berdasarkan ", " (koma spasi) seperti di SideBar
            const parts = pasted.split(', ').map(s => s.trim());
            if (parts.length === 2) {
                const lat = parseFloat(parts[0]);
                const lng = parseFloat(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) {
                    // Cegah paste default (agar tidak masuk ke satu field)
                    event.preventDefault();

                    // Isi kedua field menggunakan formatCoordinate (mengubah ke string dengan titik)
                    this.form.lat = this.formatCoordinate(lat);
                    this.form.lng = this.formatCoordinate(lng);

                    // Bersihkan error
                    this.clearFieldError('lat');
                    this.clearFieldError('lng');

                    // Pindahkan marker draggable ke koordinat baru di peta
                    this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });

                    // Terbang ke titik tersebut
                    this.$nextTick(() => {
                        this.$root.$emit('map-fly-to-pin', lat, lng, 16);
                    });

                    // Notifikasi sukses
                    this.$toast?.success('Koordinat berhasil dipisahkan', { timeout: 1500 });
                }
            }
            // Jika bukan format dua angka, biarkan paste normal (tidak dicegah)
        },
        onAddressSelected({ lat, lng }) {
            // Isi otomatis form dengan data dari hasil pencarian
            this.form.lat = this.formatCoordinate(lat);
            this.form.lng = this.formatCoordinate(lng);
            this.clearFieldError('lat');
            this.clearFieldError('lng');

            // Pindahkan marker draggable dan pusatkan peta ke lokasi baru
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
            this.$nextTick(() => {
                this.$root.$emit('map-fly-to-pin', lat, lng, 16);
            });
        },
        invalidateMap() {
            if (this.mapObject) {
                setTimeout(() => {
                    this.mapObject.invalidateSize();
                }, 100);
            }
        },
        ...mapActions('users', ['updateTechnician']),
        async applyManualCoords() {
            const lat = parseFloat(this.sanitizeCoordinate(this.form.lat));
            const lng = parseFloat(this.sanitizeCoordinate(this.form.lng));
            if (isNaN(lat) || isNaN(lng)) {
                this.$toast?.error('Latitude/Longitude tidak valid');
                return;
            }

            // 1. Pindahkan marker draggable
            this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });
            await this.$nextTick();
            this.$root.$emit('map-fly-to-pin', lat, lng, 16);

            // 2. Reverse geocoding untuk mendapatkan alamat
            try {
                this.$toast?.info('Mencari alamat...', { timeout: 1000 });
                const response = await fetch(
                    `${process.env.VUE_APP_MAP_URL}/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=id`
                );
                const data = await response.json();

                if (data && data.display_name) {
                    this.form.address = data.display_name;
                    this.clearFieldError('address');
                    this.$toast?.success('Alamat ditemukan');
                } else {
                    this.$toast?.warning('Alamat tidak ditemukan untuk koordinat ini');
                }
            } catch (err) {
                console.error('Reverse geocoding error:', err);
                this.$toast?.error('Gagal mendapatkan alamat: ' + (err.message || 'Error jaringan'));
            }
        },
        formatCoordinate(value) {
            if (value === null || value === undefined) return ''
            let str = String(value).trim().replace(/,/g, '.')
            return str
        },
        sanitizeCoordinate(value) {
            if (value === null || value === undefined) return '';
            let str = String(value).trim();
            str = str.replace(/,/g, '.');
            return str;
        },

        clearFieldError(field) {
            if (this.fieldErrors[field] !== undefined) {
                this.fieldErrors[field] = '';
            }
        },

        resetErrors() {
            this.fieldErrors = {
                area: '',
                address: '',
                lat: '',
                lng: '',
                radius: '',
            };
        },

        resetForm() {
            this.resetErrors();
            this.form.name = this.detail.name || '';
            this.form.area = this.detail.address?.area || 'West';
            this.form.address = this.detail.address?.address || '';
            this.form.lat = this.detail.address?.latitude != null
                ? this.formatCoordinate(this.detail.address.latitude)
                : (this.detail.latitude ? this.formatCoordinate(this.detail.latitude) : '');
            this.form.lng = this.detail.address?.longitude != null
                ? this.formatCoordinate(this.detail.address.longitude)
                : (this.detail.longitude ? this.formatCoordinate(this.detail.longitude) : '');
            // Perbaikan: ambil radius dari address terlebih dahulu
            const radius = this.detail.address?.radius ?? this.detail.radius;
            this.form.radius = radius !== undefined && radius !== null ? Number(radius) : 80;
        },

        // Geocoding menggunakan Nominatim (OpenStreetMap)
        async geocodeAddress() {
            if (!this.form.address || !this.form.address.trim()) {
                this.$toast?.warn('Masukkan alamat terlebih dahulu');
                return;
            }

            // Tampilkan indikator loading (opsional)
            this.$toast?.info('Mencari alamat...', { timeout: 1000 });

            try {
                const response = await fetch(
                    `${process.env.VUE_APP_MAP_URL}/search?format=json&q=${encodeURIComponent(this.form.address)}&limit=1`
                );
                const data = await response.json();

                if (data && data.length) {
                    const lat = parseFloat(data[0].lat);
                    const lng = parseFloat(data[0].lon); // Perhatikan: properti 'lon', bukan 'lng'

                    // 1. Update form
                    this.form.lat = this.formatCoordinate(lat);
                    this.form.lng = this.formatCoordinate(lng);
                    this.clearFieldError('lat');
                    this.clearFieldError('lng');

                    // 2. Update store agar marker draggable pindah ke titik baru
                    this.$store.commit('map/SET_DRAGGABLE_MARKER_POSITION', { lat, lng });

                    // 3. Kirim event ke komponen map untuk terbang & zoom
                    //    Event ini sudah didengarkan oleh LeafletTechnicianAddress
                    // Beri waktu untuk reaktivitas Vue
                    await this.$nextTick();
                    this.$root.$emit('map-fly-to-pin', lat, lng, 16);

                    // 4. Beri sedikit jeda agar map sempat merender marker baru
                    setTimeout(() => {
                        this.$toast?.success('Koordinat ditemukan, peta terpusat ke alamat');
                    }, 100);
                } else {
                    this.$toast?.error('Alamat tidak ditemukan');
                }
            } catch (err) {
                console.error('Geocoding error:', err);
                this.$toast?.error('Gagal mengambil koordinat: ' + (err.message || 'Error jaringan'));
            }
        },

        // Aktifkan mode pilih titik di peta
        enableMapPicker() {
            this.$store.commit('map/SET_PICK_MODE', true)
            this.$toast?.info('Klik pada peta untuk menentukan lokasi', { timeout: 3000 })
        },

        validateForm() {
            this.resetErrors();
            let isValid = true;

            // Validasi Area
            if (!this.form.area) {
                this.fieldErrors.area = 'Area wajib dipilih.';
                isValid = false;
            }

            // Validasi Address
            if (!this.form.address || !this.form.address.trim()) {
                this.fieldErrors.address = 'Home address wajib diisi.';
                isValid = false;
            }

            // Validasi Latitude
            const rawLat = this.sanitizeCoordinate(this.form.lat);
            if (!rawLat) {
                this.fieldErrors.lat = 'Latitude wajib diisi.';
                isValid = false;
            } else {
                const numericRegex = /^-?\d+(\.\d+)?$/;
                if (!numericRegex.test(rawLat)) {
                    this.fieldErrors.lat = 'Format Latitude tidak valid (hanya angka, minus, dan titik desimal).';
                    isValid = false;
                } else {
                    const latNum = parseFloat(rawLat);
                    if (latNum < -90 || latNum > 90) {
                        this.fieldErrors.lat = 'Latitude harus antara -90 sampai 90.';
                        isValid = false;
                    }
                }
            }

            // Validasi Longitude
            const rawLng = this.sanitizeCoordinate(this.form.lng);
            if (!rawLng) {
                this.fieldErrors.lng = 'Longitude wajib diisi.';
                isValid = false;
            } else {
                const numericRegex = /^-?\d+(\.\d+)?$/;
                if (!numericRegex.test(rawLng)) {
                    this.fieldErrors.lng = 'Format Longitude tidak valid (hanya angka, minus, dan titik desimal).';
                    isValid = false;
                } else {
                    const lngNum = parseFloat(rawLng);
                    if (lngNum < -180 || lngNum > 180) {
                        this.fieldErrors.lng = 'Longitude harus antara -180 sampai 180.';
                        isValid = false;
                    }
                }
            }

            // Validasi Radius
            const radiusValue = this.form.radius;
            if (radiusValue === null || radiusValue === undefined || radiusValue === '') {
                this.fieldErrors.radius = 'Radius wajib diisi.';
                isValid = false;
            } else {
                const radiusNum = Number(radiusValue);
                if (isNaN(radiusNum)) {
                    this.fieldErrors.radius = 'Radius harus berupa angka.';
                    isValid = false;
                } else if (radiusNum <= 0) {
                    this.fieldErrors.radius = 'Radius harus lebih besar dari 0.';
                    isValid = false;
                }
            }

            return isValid;
        },

        async saveProfile() {
            if (!this.canSave) {
                this.$toast?.error('Anda tidak memiliki akses untuk mengubah alamat teknisi.');
                return;
            }

            if (!this.validateForm()) {
                this.$toast?.error('Gagal menyimpan, periksa kembali input form Anda.');
                return;
            }

            this.saving = true;

            try {
                const payload = {
                    area: this.form.area,
                    address: this.form.address.trim(),
                    latitude: parseFloat(this.sanitizeCoordinate(this.form.lat)),
                    longitude: parseFloat(this.sanitizeCoordinate(this.form.lng)),
                    radius: Number(this.form.radius)
                };

                console.log("this.detail", this.detail, this.detail.user_id)

                await this.updateTechnician({ userId: this.detail.user_id, payload });

                // Gabungkan data lama dengan data baru
                const updatedTech = {
                    ...this.detail,                     // mempertahankan user_id, name, dll
                    ...payload,                         // area, address, latitude, longitude, radius
                    address: {                          // pastikan address sebagai objek
                        ...this.detail.address,
                        ...payload
                    }
                };

                // Commit ke store users
                this.$store.commit('users/UPDATE_TECHNICIAN', updatedTech);
                console.log('State setelah update:', this.$store.state.users.technicians.find(t => t.user_id === this.detail.user_id));
                // Update selected technician di map store
                this.$store.commit('map/SET_SELECTED_TECHNICIAN', updatedTech);

                this.$toast?.success('Alamat teknisi berhasil diperbarui.');
                this.$emit('close');
            } catch (err) {
                this.$toast?.error('Gagal menyimpan: ' + err.message);
            }
        },

        handleCancel() {
            this.resetErrors();
            this.$emit('close');
        }
    },
};
</script>

<style scoped>
.mc-tech-popup {
    font-family: inherit;
    width: 360px;
    max-width: 100%;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 100vh;
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
    z-index: 1000;
}

.mc-tech-popup__header {
    padding: 14px 16px 12px;
    border-bottom: 1px solid #eef2f7;
    background: #f8fafc;
    position: relative;
    min-height: 110px;
}

.mc-tech-popup__header.is-west {
    background: #eff6ff;
}

.mc-tech-popup__header.is-east {
    background: #f0fdf4;
}

.mc-tech-popup__title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.mc-tech-popup__name {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
}

.mc-tech-popup__status {
    margin-top: 4px;
}

.area-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.6875rem;
    font-weight: 600;
}

.badge-west {
    background: #bfdbfe;
    color: #1e3a8a;
}

.badge-east {
    background: #dcfce7;
    color: #15803d;
}

.mc-tech-popup__close {
    font-size: 1.25rem;
    background: transparent;
    border: none;
    padding: 4px;
    border-radius: 6px;
    color: #64748b;
    cursor: pointer;
}

.mc-tech-popup__close:hover {
    background: rgba(15, 23, 42, 0.08);
}

.mc-tech-popup__body {
    padding: 16px;
    flex: 1;
    overflow-y: auto;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    display: block;
    margin-bottom: 4px;
}

.form-control-sm,
.form-select-sm {
    font-size: 0.875rem;
    border-radius: 6px;
    border-color: #e5e9f0;
}

.form-control-sm:focus,
.form-select-sm:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.field-error {
    color: #dc2626;
    font-size: 0.7rem;
    margin-top: 4px;
    font-weight: 500;
}

.mc-tech-popup__footer {
    display: flex;
    gap: 6px;
    padding: 12px 16px;
    background: #f8fafc;
    border-top: 1px solid #eef2f7;
}

.mc-tech-popup__action {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    background: #ffffff;
    border: 1px solid #e5e9f0;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    transition: all 0.15s ease;
}

.mc-tech-popup__action--primary {
    background: #2563eb;
    border-color: #2563eb;
    color: #ffffff;
}

.mc-tech-popup__action--primary:hover {
    background: #1d4ed8;
}

.mc-tech-popup__action:active {
    transform: translateY(1px);
}

.required-mark {
    color: #dc2626;
    font-weight: 700;
}
</style>
